import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown, HardHat, Plus, Settings2, ShieldCheck, SlidersHorizontal, Store, UserCog, X,
} from 'lucide-react';
import {
  fetchPortalAccess, savePortalAccess, listWorkers, setWorkerGroup, createWorker, createWorkerLogin,
} from '../api/resources';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import Toggle from '../components/ui/Toggle';
import Modal from '../components/ui/Modal';

/**
 * Who your people are, and what each kind of them may open.
 *
 * One row per account group. The row opens to show the people in it, and
 * "Manage access" sets what the whole group may do — so access is decided once
 * per group, not once per person. Everyone here is a worker record either way,
 * so moving someone between groups never touches their jobs or their pay.
 */

// The look of each group. The groups themselves come from the server, so the
// two can never drift apart; this only says which icon and colour to use.
const LOOK = {
  worker: { Icon: HardHat, chip: 'bg-brand-600', ring: 'border-brand-500 bg-brand-50/30', text: 'text-brand-700' },
  supervisor: { Icon: ShieldCheck, chip: 'bg-success-600', ring: 'border-success-500 bg-success-50/30', text: 'text-success-700' },
  storeManager: { Icon: Store, chip: 'bg-accent-600', ring: 'border-accent-500 bg-accent-50/30', text: 'text-accent-700' },
  workshopManager: { Icon: UserCog, chip: 'bg-info-600', ring: 'border-info-500 bg-info-50/30', text: 'text-info-700' },
};
const look = (key) => LOOK[key] || LOOK.worker;

export default function PortalAccess() {
  const qc = useQueryClient();
  const { companyId, role } = useAuth();
  const { success, error } = useToast();
  const isAdmin = role === 'ADMIN';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['portal-access', companyId],
    queryFn: () => fetchPortalAccess(companyId),
    enabled: !!companyId,
  });
  const { data: workers } = useQuery({ queryKey: ['workers'], queryFn: () => listWorkers() });

  const [draft, setDraft] = useState(null);
  useEffect(() => { if (data?.access) setDraft(data.access); }, [data]);

  // Which group row is open, which one's switches are being set, and which
  // one is being added to.
  const [openGroup, setOpenGroup] = useState(null);
  const [accessFor, setAccessFor] = useState(null);
  const [addTo, setAddTo] = useState(null);
  const [tab, setTab] = useState('existing'); // existing | new
  const [query, setQuery] = useState('');
  const [fresh, setFresh] = useState({ name: '', phone: '', role: 'SEWING_TAILOR', username: '', password: '' });

  const fail = (e, fallback) => error(e.response?.data?.message || fallback);

  const saveMut = useMutation({
    mutationFn: (body) => savePortalAccess(companyId, body),
    onSuccess: (d) => {
      success(d.message || 'Saved.');
      qc.setQueryData(['portal-access', companyId], (old) => ({ ...(old || {}), access: d.access }));
    },
    onError: (e) => fail(e, 'Could not save this.'),
  });

  const groupMut = useMutation({
    mutationFn: ({ id, group }) => setWorkerGroup(id, group),
    onSuccess: (d) => { success(d.message || 'Saved.'); qc.invalidateQueries({ queryKey: ['workers'] }); },
    onError: (e) => fail(e, 'Could not move them.'),
  });

  // A brand-new person: the worker record, their login, then their group.
  const newMut = useMutation({
    mutationFn: async ({ group, ...body }) => {
      const worker = await createWorker({ name: body.name, phone: body.phone, role: body.role });
      await createWorkerLogin(worker.id, { username: body.username, password: body.password });
      if (group !== 'worker') await setWorkerGroup(worker.id, group);
      return { worker, group };
    },
    onSuccess: ({ worker }) => {
      success(`${worker.name} added.`);
      setAddTo(null);
      setFresh({ name: '', phone: '', role: 'SEWING_TAILOR', username: '', password: '' });
      qc.invalidateQueries({ queryKey: ['workers'] });
    },
    onError: (e) => fail(e, 'Could not add them.'),
  });

  if (isLoading || !draft) return <Spinner label="Loading who can see what…" />;
  if (isError) return <div className="card p-10 text-center text-sm text-content-muted">Could not load portal access.</div>;

  const features = data.features || [];
  const groups = data.groups || [];

  const set = (group, key, value) => {
    const next = { ...draft, [group]: { ...draft[group], [key]: value } };
    // A switch that depends on another cannot stand on its own.
    for (const f of features) {
      if (f.needs && !next[group][f.needs]) next[group][f.key] = false;
    }
    setDraft(next);
    saveMut.mutate(next);
  };

  const active = (workers || []).filter((w) => w.status === 'ACTIVE');
  const peopleIn = (key) => active.filter((w) => w.group === key);
  // Someone can only be moved if they have a login — the group decides what a
  // login opens, so there has to be one.
  const movable = (key) => active.filter((w) => w.hasLogin && w.group !== key
    && w.name.toLowerCase().includes(query.trim().toLowerCase()));

  const onCount = (key) => features.filter((f) => draft[key]?.[f.key]).length;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={SlidersHorizontal}
        title="Portal Access"
        subtitle="Your people, grouped — and what each group may open when they sign in"
      />

      {!isAdmin && (
        <p className="rounded-lg border border-warning-500 bg-warning-50/40 px-3 py-2 text-xs text-content-muted">
          Only an admin can change these.
        </p>
      )}

      <div className="space-y-3">
        {groups.map((g) => {
          const L = look(g.key);
          const people = peopleIn(g.key);
          const open = openGroup === g.key;
          return (
            <div key={g.key} className="card overflow-hidden">
              {/* ---- the group row ---- */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:px-4">
                <button
                  type="button"
                  className="-ml-1 flex min-w-0 flex-1 items-center gap-2.5 rounded px-1 py-0.5 text-left hover:bg-surface-hover"
                  aria-expanded={open}
                  onClick={() => setOpenGroup(open ? null : g.key)}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white ${L.chip}`}>
                    <L.Icon size={16} />
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <b className="truncate text-sm text-content">{g.label}</b>
                      <span className="rounded-full bg-surface-sunken px-1.5 py-0.5 text-2xs font-bold text-content-muted">
                        {people.length}
                      </span>
                      <ChevronDown size={14} className={`shrink-0 text-content-subtle transition-transform ${open ? '' : '-rotate-90'}`} />
                    </span>
                    <span className="block truncate text-xs text-content-muted">{g.blurb}</span>
                  </span>
                </button>

                <span className="flex items-center gap-1.5">
                  <span className="hidden text-2xs text-content-subtle sm:inline">
                    {onCount(g.key)} of {features.length} on
                  </span>
                  <button className="btn-outline btn-sm" onClick={() => setAccessFor(g.key)}>
                    <Settings2 size={13} /> Manage access
                  </button>
                  {isAdmin && (
                    <button
                      className="btn-outline btn-sm"
                      onClick={() => {
                        setAddTo(g.key);
                        setTab('existing');
                        setQuery('');
                        // Only the floor is assumed to sew. Everybody else is
                        // being added to run something, so the trade starts
                        // where it almost always ends up.
                        setFresh((f) => ({ ...f, role: g.key === 'worker' ? 'SEWING_TAILOR' : 'NO_TRADE' }));
                      }}
                      title={`Add someone to ${g.label}`}
                    >
                      <Plus size={13} /> Add
                    </button>
                  )}
                </span>
              </div>

              {/* ---- the people in it ---- */}
              {open && (
                <div className="border-t border-line bg-surface-sunken/40 px-3 py-3 sm:px-4">
                  {people.length === 0 ? (
                    <p className="py-4 text-center text-sm italic text-content-subtle">
                      Nobody in {g.label} yet — use “Add”.
                    </p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {people.map((w) => (
                        <div key={w.id} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 ${L.ring}`}>
                          <L.Icon size={15} className={`shrink-0 ${L.text}`} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-content">{w.name}</span>
                            <span className="block truncate text-2xs text-content-subtle">
                              {w.hasLogin ? `signs in as ${w.loginName}` : 'no login yet'}
                            </span>
                          </span>
                          {isAdmin && g.key !== 'worker' && (
                            <button
                              className="btn-icon hover:bg-danger-50 hover:text-danger-600"
                              title={`${w.name} goes back to Workers`}
                              disabled={groupMut.isPending}
                              onClick={() => groupMut.mutate({ id: w.id, group: 'worker' })}
                            >
                              <X size={15} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ---- Manage access: the group's switches ---- */}
      <Modal
        open={!!accessFor}
        onClose={() => setAccessFor(null)}
        title={accessFor ? `${groups.find((g) => g.key === accessFor)?.label} — access` : 'Access'}
        subtitle="Everyone in this group, now and later"
        size="sm"
      >
        <div className="divide-y divide-line">
          {accessFor && features.map((f) => (
            <div key={f.key} className="flex items-start gap-3 py-3">
              <Toggle
                checked={!!draft[accessFor][f.key]}
                disabled={!isAdmin || (f.needs && !draft[accessFor][f.needs])}
                onChange={(v) => set(accessFor, f.key, v)}
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-content">{f.label}</span>
                <span className="block text-xs text-content-muted">{f.blurb}</span>
              </span>
            </div>
          ))}
        </div>
      </Modal>

      {/* ---- Add someone to a group ---- */}
      <Modal
        open={!!addTo}
        onClose={() => setAddTo(null)}
        title={addTo ? `Add to ${groups.find((g) => g.key === addTo)?.label}` : 'Add'}
        size="sm"
        footer={tab === 'new' ? (
          <>
            <button className="btn-outline" onClick={() => setAddTo(null)}>Cancel</button>
            <button
              className="btn-primary"
              disabled={newMut.isPending || !fresh.name.trim() || !fresh.username.trim() || fresh.password.length < 8}
              onClick={() => newMut.mutate({ ...fresh, name: fresh.name.trim(), username: fresh.username.trim(), group: addTo })}
            >
              {newMut.isPending ? 'Adding…' : 'Add person'}
            </button>
          </>
        ) : null}
      >
        {/* Someone you already have, or someone new — who is added as a worker
            in this group, since everyone here is a worker record. */}
        <div className="mb-3 inline-flex rounded-md border border-line bg-surface-sunken p-0.5" role="group">
          {[['existing', 'Choose from workers'], ['new', 'Add someone new']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={tab === key}
              onClick={() => setTab(key)}
              className={`rounded px-2.5 py-1 text-xs font-semibold ${
                tab === key ? 'bg-surface text-brand-700 shadow-xs' : 'text-content-muted hover:text-content'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'existing' ? (
          <>
            <input
              className="input"
              autoFocus
              placeholder="Search your workers…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
              {movable(addTo).length === 0 ? (
                <p className="py-6 text-center text-sm italic text-content-subtle">
                  {query
                    ? `No worker with a login matches “${query}”.`
                    : 'Nobody with a login is free. Add someone new, or create a login on a worker’s page.'}
                </p>
              ) : movable(addTo).map((w) => (
                <button
                  key={w.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg border border-line px-3 py-2 text-left hover:border-brand-500 hover:bg-brand-50/40"
                  disabled={groupMut.isPending}
                  onClick={() => { groupMut.mutate({ id: w.id, group: addTo }); setAddTo(null); setQuery(''); }}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-content">{w.name}</span>
                    <span className="block text-2xs text-content-subtle">
                      now in {groups.find((g) => g.key === w.group)?.label || 'no group'} · signs in as {w.loginName}
                    </span>
                  </span>
                  <Plus size={15} className="shrink-0 text-brand-600" />
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-content-muted">
              They are added to your workers as well, so their jobs and pay work like anyone else’s.
            </p>
            <label className="block">
              <span className="label">Name</span>
              <input
                className="input"
                autoFocus
                value={fresh.name}
                onChange={(e) => setFresh((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="label">Phone</span>
                <input
                  className="input"
                  value={fresh.phone}
                  onChange={(e) => setFresh((f) => ({ ...f, phone: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="label">Trade</span>
                <select
                  className="input"
                  value={fresh.role}
                  onChange={(e) => setFresh((f) => ({ ...f, role: e.target.value }))}
                >
                  <option value="SEWING_TAILOR">Sewing tailor</option>
                  <option value="CUTTING_MASTER">Cutting master</option>
                  <option value="ALL_ROUNDER">All rounder</option>
                  {/* A supervisor or a manager is on the books so their login
                      and their pay work — but no garment is handed to them. */}
                  <option value="NO_TRADE">Other</option>
                </select>
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="label">Signs in as</span>
                <input
                  className="input"
                  placeholder="e.g. ramesh"
                  value={fresh.username}
                  onChange={(e) => setFresh((f) => ({ ...f, username: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="label">Password</span>
                <input
                  className="input"
                  placeholder="At least 8 characters"
                  value={fresh.password}
                  onChange={(e) => setFresh((f) => ({ ...f, password: e.target.value }))}
                />
              </label>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
